// PMS — season bands: the pure mapping from a date to its season and closure,
// shared by the Planning band and the Tarifs timeline so both always agree.
//
// Fiscal year is named by its END: 2027 = 2026-12-01 → 2027-11-30 (the SAS
// year, PMS-SEASON-CALENDAR-BRIEF §2.2). Colours live here once, not in CSS,
// so the timeline, the Planning band and the legend cannot drift apart.

import { RATE_SEASON_LABELS_FR, type RateSeason, type StoredSeasonCalendar } from '@lemontaigu/pms-core';

export type SeasonCal = Pick<StoredSeasonCalendar, 'year' | 'periods' | 'closures'>;

/** Cool → hot, so the eye reads the peak without a legend. Muted enough to sit
 *  behind text in both themes. */
export const SEASON_COLORS: Record<RateSeason, string> = {
  basse: '#a9cbe8',
  moyenne: '#bfdcb0',
  haute: '#f6c788',
  tresHaute: '#e7877e',
};

/** Shut for guests — drawn as a hatch over whatever season sits underneath. */
export const CLOSED_COLOR = '#cfc8b8';

/** One-letter-ish tag for narrow cells, where the full label cannot fit. */
export const SEASON_SHORT: Record<RateSeason, string> = {
  basse: 'B',
  moyenne: 'M',
  haute: 'H',
  tresHaute: 'TH',
};

/** Fiscal year of an ISO date: December belongs to the NEXT year's name. */
export function fiscalYearOf(iso: string): number {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return m >= 12 ? y + 1 : y;
}

export interface DayBand {
  season: RateSeason | null;
  closed: boolean;
  /** « Haute — Vacances d'hiver » style, for the hover title. */
  title: string;
}

/** What a single day is, across the calendars the caller has loaded. */
export function bandForDay(cals: SeasonCal[], iso: string): DayBand {
  const cal = cals.find((c) => c.year === fiscalYearOf(iso));
  const period = cal?.periods.find((p) => iso >= p.from && iso <= p.to) ?? null;
  const closure = cal?.closures.find((c) => iso >= c.from && iso <= c.to) ?? null;
  const parts: string[] = [];
  if (period) parts.push(`${RATE_SEASON_LABELS_FR[period.season]}${period.description ? ` — ${period.description}` : ''}`);
  if (closure) parts.push(`Fermé${closure.reason ? ` (${closure.reason})` : ''}`);
  if (!period && !closure) parts.push('Aucune saison saisie');
  return { season: period?.season ?? null, closed: closure !== null, title: parts.join(' · ') };
}

/** Inclusive day count between two ISO dates. */
export function daysInclusive(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Offset of `iso` from `origin`, in whole days (0 = origin). */
export function dayOffset(origin: string, iso: string): number {
  return daysInclusive(origin, iso) - 1;
}

/** The fiscal year's first and last day, as ISO dates. */
export function fiscalYearBounds(year: number): { from: string; to: string } {
  return { from: `${year - 1}-12-01`, to: `${year}-11-30` };
}
